<template>
    <FKBreadCrumb :page="$t('page.therapy_trend')">
        <FKBreadCrumbItem :page="$t('page.patients')" to="/patients" />
        <FKBreadCrumbItem :page="$t('page.patient')" :to="`/patient/${patient_id}`" />
    </FKBreadCrumb>
    <!-- CHART -->
    <div class="card mb-25 border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-sm-25 pt-lg-30 pb-lg-30 ps-lg-25 pe-lg-25">
            <h5 class="fs-18 fw-semibold text-black mb-5 mb-md-8">{{ $t('feature.therapy.overall_adherence') }}</h5>
            <div class="d-flex gap-3 justify-content-between align-items-start">
                <div class="overall-range">
                    <span class="fs-12 text-body-tertiary text-uppercase">{{ $t("feature.therapy.$range.from")
                        }}:</span>
                    <span class="px-1">{{ range_from }}</span>
                    <span class="fs-12 text-body-tertiary text-uppercase">{{ $t("feature.therapy.$range.to") }}:</span>
                    <span class="d-inline-flex gap-2">
                        <div class="btn-group btn-group-sm" role="group">
                            <button type="button" class="btn btn-outline-light border-0 fw-light px-1 py-0 m-0"
                                :class="range_to_selected == range_to ? 'bg-dark-subtle' : ''"
                                @click="update_range(range_to)">{{ range_to }}</button>
                            <button type="button" class="btn btn-outline-light border-0 fw-light px-1 py-0 m-0"
                                :class="range_to_selected == range_today ? 'bg-dark-subtle' : ''"
                                @click="update_range(range_today)">Today</button>
                        </div>
                        <!-- <span class="cursor-pointer" @click="update_range(range_to)">{{ range_to }}</span><span class="cursor-pointer" @click="update_range(range_today)">Today</span> -->
                    </span>
                </div>
                <div class="form-group">
                    <select class="form-select shadow-none text-black p-15 pe-50 bg-body-tertiary fs-14 border-0"
                        :disabled="loading" required @change="loadTrends" :value="range_type">
                        <option value="day" v-if="single_therapy">{{ $t("feature.therapy.$trend_range.$frequency.day")
                            }}</option>
                        <option value="week">{{ $t("feature.therapy.$trend_range.$frequency.week") }}</option>
                        <option value="month">{{ $t("feature.therapy.$trend_range.$frequency.month") }}</option>
                    </select>
                </div>
            </div>
            <SpinnerLoading v-if="loading" class="mt-25" />
            <div class="chart mb-25" v-else>
                <p v-if="empty_data">
                    {{ $t("feature.therapy.no_data") }}
                </p>
                <apexchart v-else height="350" id="adherence_chart" :options="lineOptions"
                    :series="adherencesLineSeries">
                </apexchart>
            </div>
            <!-- <div class="chart">
                <apexchart height="350" id="counters_chart" :options="lineOptions" :series="countLineSeries">
                </apexchart>
            </div> -->
        </div>
    </div>
    <div class="card mb-25 border-0 rounded-0 bg-white" v-if="!loading && !empty_data">
        <div class="card-body p-15 p-sm-20 p-sm-25 pt-lg-30 pb-lg-30 ps-lg-25 pe-lg-25">
            <div class="table-responsive">
                <table class="table text-nowrap align-middle mb-0">
                    <!-- HEADER -->
                    <thead>
                        <tr>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-12 pt-0">
                            </th>

                            <th scope="col"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                                {{ $t('feature.therapy.drug') }}
                            </th>
                            <th scope="col"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                                {{ $t('feature.therapy.adherence') }}
                            </th>
                            <th scope="col"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                                {{ $t('feature.therapy.intake.expected') }}
                            </th>
                            <th scope="col"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                                {{ $t('feature.therapy.intake.taken') }}
                            </th>
                            <th scope="col"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                                {{ $t('feature.therapy.intake.taken_in_time') }}
                            </th>
                            <th scope="col"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                                {{ $t('feature.therapy.intake.taken_not_in_time') }}
                            </th>
                            <th scope="col"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                                {{ $t('feature.therapy.intake.not_taken') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(ranges, range_index) in range_groups" :key="range_index">
                            <tr>
                                <td class="shadow-none lh-1 fw-medium text-primary border-bottom-0 border-top-0 bg-light-subtle text-uppercase"
                                    colspan="8">
                                    <template
                                        v-if="ranges_start(ranges) !== undefined && ranges_end(ranges) !== undefined">
                                        {{ display_range(range_type, ranges_start(ranges)!, ranges_end(ranges)!) }}
                                    </template>
                                    <template v-else>
                                        {{ $t(`feature.therapy.$trend_range.${range_type}`) }} {{
                                            range_index }}
                                    </template>
                                    <!-- <template v-if="range_type === 'day'">
                                        <template v-if="ranges_start(ranges) !== undefined">
                                            {{ DateFormat.DAY_FULL_MONTH(ranges_start(ranges)!) }}
                                        </template>
                                        <template v-else>
                                            {{ $t(`feature.therapy.$trend_range.${range_type}`) }} {{
                                                range_index }}
                                        </template>
                                    </template>
                                    <template v-if="range_type === 'week'">
                                        {{ $t(`feature.therapy.$trend_range.${range_type}`) }} {{
                                            range_index }}
                                        <template
                                            v-if="ranges_start(ranges) !== undefined && ranges_end(ranges) !== undefined">
                                            <span class="small ms-3 d-inline-flex gap-1">
                                                {{ DateFormat.DAY_ABBR_MONTH(ranges_start(ranges)!) }}
                                                <i class="ph ph-arrow-right"></i>
                                                {{ DateFormat.DAY_ABBR_MONTH(ranges_end(ranges)!) }}
                                            </span>
                                        </template>
                                    </template>
                                    <template v-if="range_type === 'month'">
                                        <template v-if="ranges_start(ranges)! !== undefined">
                                            {{ DateFormat.FULL_MONTH(ranges_start(ranges)!) }}
                                        </template>
                                        <template v-else>
                                            {{ $t(`feature.therapy.$trend_range.${range_type}`) }} {{
                                                range_index }}
                                        </template>
                                    </template> -->
                                </td>
                            </tr>
                            <tr v-for="(trend, index) in ranges" :key="index"
                                :class="[{ 'border-bottom': index !== 0 && index === ranges.length - 1 }]">
                                <td class="shadow-none lh-1 fw-medium text-primary border-bottom-0">
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ trend.drug }}
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ trend.adherence }}%
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ expected(trend) }}
                                </td>
                                <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                                    {{ trend.taken }}
                                </td>
                                <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                                    {{ trend.in_time }}
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ trend.not_in_time }}
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ trend.not_taken }}
                                </td>
                            </tr>
                            <tr v-if="ranges.length !== 1" class="border-bottom border-light-subtle">
                                <td class="shadow-none lh-1 fw-medium text-primary">
                                    <div class="opacity-25 text-body-tertiary fs-12 text-uppercase">
                                        {{ $t(`feature.therapy.$trend_range.${range_type}`).toUpperCase() }} {{
                                            range_index }}<br>
                                    </div>
                                </td>
                                <td
                                    class="shadow-none lh-1 fw-normal text-body-tertiary text-end fs-12 text-uppercase opacity-25 text-body-tertiary">
                                    {{ $t('feature.therapy.intake.subtotal') }}
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ total_adherence(ranges) }}%
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ total_intakes_expected(ranges) }}
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ total_intakes_taken(ranges) }}
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ total_intakes_taken_in_time(ranges) }}
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ total_intakes_taken_not_time(ranges) }}
                                </td>
                                <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end">
                                    {{ total_intakes_not_taken(ranges) }}
                                </td>

                            </tr>
                        </template>
                        <tr class="opacity-75 border-0 border-5 border-light border-top">
                            <td scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary">
                                {{ $t('feature.therapy.intake.total') }}
                            </td>
                            <td class="shadow-none lh-1 fw-normal text-body-tertiary text-end"></td>
                            <td class="shadow-none lh-1 fw-bold text-end">
                                {{ total_adherence(therapy_trends) }}%
                            </td>
                            <td class="shadow-none lh-1 fw-bold text-body-tertiary text-end">
                                {{ total_intakes_expected(therapy_trends) }}
                            </td>
                            <td class="shadow-none lh-1 fw-bold text-body-tertiary text-end">
                                {{ total_intakes_taken(therapy_trends) }}
                            </td>
                            <td class="shadow-none lh-1 fw-bold text-body-tertiary text-end">
                                {{ total_intakes_taken_in_time(therapy_trends) }}
                            </td>
                            <td class="shadow-none lh-1 fw-bold text-body-tertiary text-end">
                                {{ total_intakes_taken_not_time(therapy_trends) }}
                            </td>
                            <td class="shadow-none lh-1 fw-bold text-body-tertiary text-end">
                                {{ total_intakes_not_taken(therapy_trends) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.overall-range {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 10px;
    align-items: end;
}
</style>
<script lang="ts">
import { defineComponent } from "vue";

import FKBreadCrumb from "@/components/Widgets/BreadCrumb/FKBreadCrumb.vue";
import FKBreadCrumbItem from "@/components/Widgets/BreadCrumb/FKBreadCrumbItem.vue";
import CONFIG from "@/utils/const/config"
import { TherapiesStatisticsRangeType, TherapyTrend } from '@/utils/models/therapy.model'
import { ApexOptions } from 'apexcharts';
import DateFormatUtils from '@/utils/methods/date_formats';
import API from "@/services/api/api.service";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import dayjs from "dayjs";

export default defineComponent({
    name: "PatientPage",
    components: {
        FKBreadCrumb,
        FKBreadCrumbItem,
        SpinnerLoading
    },
    data() {
        return {
            therapy_trends: this.$route.meta.therapy as TherapyTrend[],
            range_type: this.$route.query.range_type as TherapiesStatisticsRangeType,
            loading: false,
            range_to_selected: this.$route.query.to as string,
        }
    },
    computed: {
        config() { return CONFIG },
        patient_id(): string { return this.$route.params.id as string },
        range_from(): string { return this.$route.query.from as string },
        range_to(): string { return this.$route.query.to as string },
        range_today(): string { return DateFormatUtils.YYYYMMDD(dayjs().toDate()) },
        DateFormat() {
            return DateFormatUtils
        },
        range_groups(): { [key: number]: TherapyTrend[] } {
            return ((this.therapy_trends ?? []) as TherapyTrend[]).reduce((acc, cur) => {
                const range = cur.range;
                return { ...acc, [range]: [...(acc[range] || []), cur] };
            }, {} as { [key: number]: TherapyTrend[] });
        },
        drug_groups(): { [key: number]: TherapyTrend[] } {
            return ((this.therapy_trends ?? []) as TherapyTrend[]).reduce((acc, cur) => {
                const drug = cur.drug;
                return { ...acc, [drug]: [...(acc[drug] || []), cur] };
            }, {} as { [key: number]: TherapyTrend[] });
        },
        single_therapy(): boolean {
            return this.$route.params.therapy_id !== undefined;
        },
        empty_data(): boolean {
            return Object.keys(this.drug_groups).length === 0;
        },
        // SERIES
        adherencesLineSeries(): any {
            return Object.keys(this.drug_groups).map((key, drug) => {
                return {
                    name: key,
                    data: this.drug_groups[key].map((trend) => { return { y: trend.adherence, x: trend.range } })
                }
            })
        },
        adherenceLineSeries(): any {
            return [{
                name: this.$t('feature.therapy.adherence'),
                data: this.therapy_trends.map((trend) => trend.adherence)
            }]
        },
        countLineSeries(): any {
            return [
                {
                    name: this.$t('feature.therapy.intake.$status.1'),
                    data: this.therapy_trends.map((trend) => trend.taken)
                },
                {
                    name: this.$t('feature.therapy.intake.taken_in_time'),
                    data: this.therapy_trends.map((trend) => trend.in_time)
                },
                {
                    name: this.$t('feature.therapy.intake.$status.2'),
                    data: this.therapy_trends.map((trend) => trend.not_in_time)
                },
                {
                    name: this.$t('feature.therapy.intake.$status.3'),
                    data: this.therapy_trends.map((trend) => trend.not_taken)
                },
            ]
        },
        // OPTIONS
        lineOptions(): ApexOptions {
            return {
                type: 'line',
                chart: {
                    zoom: {
                        enabled: false,
                    },
                    toolbar: {
                        show: false,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: "straight",
                    width: 4
                },

                colors: ["#006EAB", "#06b48a", '#f3c44c', '#dc3545', "#36adb2", "#2b2a3f"],
                legend: {
                    position: "top",
                    fontSize: "12px",
                    fontWeight: 500,
                    horizontalAlign: "center",
                    labels: {
                        colors: "#8E8DA1",
                    },
                    itemMargin: {
                        vertical: 15,
                        horizontal: 12,
                    },
                    show: true,
                    showForNullSeries: true,
                },
                grid: {
                    show: true,
                    strokeDashArray: 5,
                    borderColor: "#d9e9ef",
                },
                tooltip: {
                    enabled: true,
                    shared: true,
                    hideEmptySeries: false,
                    style: {
                        fontSize: "12px",
                    },
                    x: {
                        show: true,
                        formatter: (val, options) => {
                            let ranges = this.range_groups[val];
                            let start = this.ranges_start(ranges ?? []);
                            let end = this.ranges_end(ranges);
                            let title = (start === undefined || end === undefined) ?
                                `${this.$t(`feature.therapy.$trend_range.${this.range_type}`).toUpperCase()} ${val}` :
                                this.display_range(this.range_type, start, end)
                            return title?.toUpperCase();
                        },
                    },
                    y: {
                        show: true,
                        formatter: (val, { series, seriesIndex, dataPointIndex, w }) => `${val}%`,
                    },

                },
                xaxis: {
                    type: 'numeric',
                    tickAmount: 'dataPoints',
                    stepSize: 1,
                    labels: {
                        format: 'number',
                        formatter: (val, _, options) => {
                            let ranges = this.range_groups[val];
                            let start = this.ranges_start(ranges ?? []);
                            let end = this.ranges_end(ranges ?? []);
                            if ((start === undefined || end === undefined || options?.i === undefined)) {
                                return '';
                            } else {
                                if (
                                    (options.i === 0 || options.i === Object.keys(this.range_groups).length - 1) ||
                                    (this.range_type !== 'day') ||
                                    (options.i % 7 === 0)
                                ) {
                                    return this.display_range_short(this.range_type, start!, end!).toUpperCase();
                                }
                                else {
                                    return ''
                                }
                            };
                        },
                        show: true,
                        showDuplicates: false,
                        offsetX: 0,
                        style: {
                            colors: "#8e8da1",
                            fontSize: "10px",
                            fontWeight: 500,
                        },
                    },
                    axisTicks: {
                        show: true,
                    },
                    axisBorder: {
                        show: false,
                    },
                    tooltip: {
                        enabled: false,
                    }
                },
                yaxis: [{
                    max: 100,
                    show: true,
                    showForNullSeries: true,
                    showAlways: true,
                    labels: {
                        formatter(val, opts) {
                            return val.toFixed() + '%'
                        },
                        style: {
                            colors: ["#8e8da1"],
                            fontSize: "10px",

                        },

                    },
                    axisTicks: {
                        show: true,
                    },
                    axisBorder: {
                        show: false,
                    },
                    tooltip: {
                        enabled: false
                    }
                },],
            } as ApexOptions
        },
    },
    methods: {
        loadTrends(event) {
            this.loading = true;
            let prev = this.range_type;
            let type = event.target.value;
            API.getTherapyTrend({ patient_id: this.$route.params.id as string, therapy_id: this.$route.params.therapy_id as string | undefined, range_type: type, date_from: this.$route.query.from as string, date_to: this.range_to_selected }).then((response) => {
                if (response.data.success === true) {
                    this.range_type = type;
                    this.therapy_trends = response.data.data;
                } else {
                    event.target.value = prev;
                    this.range_type = prev;
                }
            }).catch((error) => {
                event.target.value = prev;
                this.range_type = prev;
            }).finally(() => {
                this.loading = false;
            });
        },
        update_range(range_to: string) {
            this.loading = true;
            this.range_to_selected = range_to;
            API.getTherapyTrend({ patient_id: this.$route.params.id as string, therapy_id: this.$route.params.therapy_id as string | undefined, range_type: this.range_type, date_from: this.$route.query.from as string, date_to: this.range_to_selected }).then((response) => {
                if (response.data.success === true) {
                    this.therapy_trends = response.data.data;
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        expected(trend: TherapyTrend) {
            return trend.taken + trend.not_taken + trend.waiting_response
        },
        /* AGGREGATE */
        total_intakes_taken(trends: TherapyTrend[]): number {
            return trends.map((trend) => trend.taken).reduce((a, b) => { return a + b }) ?? 0;
        },
        total_intakes_taken_in_time(trends: TherapyTrend[]): number {
            return trends.map((trend) => trend.in_time).reduce((a, b) => { return a + b });
        },
        total_intakes_taken_not_time(trends: TherapyTrend[]): number {
            return trends.map((trend) => trend.not_in_time).reduce((a, b) => { return a + b });
        },
        total_intakes_not_taken(trends: TherapyTrend[]): number {
            return trends.map((trend) => trend.not_taken).reduce((a, b) => { return a + b });
        },
        total_intakes_expected(trends: TherapyTrend[]): number {
            return trends.map((trend) => this.expected(trend)).reduce((a, b) => { return a + b });
        },
        total_adherence(trends: TherapyTrend[]): string {
            return ((trends.map((trend) => trend.adherence).reduce((a, b) => { return a + b })) / trends.length).toFixed(2);
        },
        /* RANGE DATES */
        ranges_start(ranges?: TherapyTrend[]): Date | undefined {
            return ranges?.find((range) => range.range_start !== undefined)?.range_start
        },
        ranges_end(ranges: TherapyTrend[]): Date | undefined {
            return ranges?.find((range) => range.range_end !== undefined)?.range_end
        },
        display_range(range_type: TherapiesStatisticsRangeType, start: Date, end: Date) {
            switch (range_type) {
                case 'day':
                    return DateFormatUtils.DAY_MONTH(start)
                case 'week':
                    return `${DateFormatUtils.DAY_MONTH(start)} → ${DateFormatUtils.DAY_MONTH(end)}`
                case 'month':
                    return DateFormatUtils.MONTH(start)
                default:
                    break;
            }
        },
        display_range_short(range_type: TherapiesStatisticsRangeType, start: Date, end: Date): string {
            switch (range_type) {
                case 'day':
                    return DateFormatUtils.DAY_MONTH_SHORT(start)
                case 'week':
                    return DateFormatUtils.DAY_MONTH_SHORT(start)
                case 'month':
                    return DateFormatUtils.MONTH_SHORT(start)
                default:
                    return '';
            }
        }
    }
});
</script>
